import React, { useState, useMemo, useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Section from '../components/Section';
import CompanyLogos from '../components/CompanyLogos';
import ServiceCard from '../components/ServiceCard';
import TeamMember from '../components/TeamMember';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ArrowRight, Mail, Phone, MapPin, Calendar, Sparkles, Search, CreditCard, Lightbulb, Check, Clipboard, ThumbsUp, ThumbsDown, Info, Linkedin, Users, Target, Heart } from 'lucide-react';
import Button from '../components/Button';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';
import FeatureTile from '../components/FeatureTile';
import NeetoCalEmbed from '../components/NeetoCalEmbed';
import ContactForm from '../components/ContactForm';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { useInView } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import NewsletterSubscribe from '../components/NewsletterSubscribe';

const teamMembers = [
  {
    name: 'Srđan Mijušković',
    title: 'Product manager',
    avatar: '/team/srdjan.png',
    linkedin: 'https://www.linkedin.com/in/srdjanmijuskovic/',
    email: 'srdjan@synorio.com',
    description: 'Verujem da dobar proizvod nastaje tamo gde se susretnu znanje, radoznalost i spremnost da se nešto zaista reši, a ne da se uradi zbog forme. Kroz Synorio radim sa timovima koji žele da AI, analitiku i automatizaciju koriste smisleno – da bolje razumeju podatke, donose odluke sa više sigurnosti i ubrzaju razvoj. Moj motiv je da pomognem ljudima da rade pametnije, a ne više. Da prepoznaju gde stvarno prave vrednost i da im u tome tehnologija ne bude prepreka, već oslonac.'
  },
  {
    name: 'Aleksandar Šujić',
    title: 'Data engineer',
    avatar: '/team/aleksandar.png',
    linkedin: 'https://www.linkedin.com/in/aleksandarsujic/',
    email: 'aleksandar@synorio.com',
    description: 'Ja sam posvećeni data inženjer sa višegodišnjim iskustvom u razvoju data arhitekture i rešenja. Radim na implementaciji i primeni i open-source data platforme koje mogu da obrade velike količine podataka za različite poslovne potrebe.'
  },
  {
    name: 'Vukašin Tadić',
    title: 'Data analyst',
    avatar: '/team/vukasin.png',
    linkedin: 'https://www.linkedin.com/in/vuka%C5%A1in-tadi%C4%87-27085a186/',
    email: 'vukasin@synorio.com',
    description: 'Pomažem timovima da dođu do pametnijih, bržih i preciznijih odluka kroz jasno definisan analitički rad i korišćenje relevantnih podataka. Kroz iskustvo u osiguranju, oglasnicima i digitalnim proizvodima, razvijao sam analitičke sisteme koji imaju direktan uticaj na poslovne rezultate.'
  }
];

const features = [
  {
    key: 'ai',
    icon: (
      <svg width="36" height="36" fill="none" viewBox="0 0 24 24" className="text-synorio-purple"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="currentColor"/></svg>
    ),
    title: 'AI',
    desc: 'Veštačka inteligencija kao goruća tema za svaki biznis može biti veoma kompleksna za razumeti. Kao stručnjaci tu smo da vam pomognemo kako da pronađete odgovore na sva pitanja tako da razvijete ili u potpunosti implementirate neko od rešenja. ',
    bullets: [
      'Prediktivna analitika',
      'ML modeli',
      'AI integrisana rešenja',
      'Chatbot implementacije',
    ],
  },
  {
    key: 'analytics',
    icon: (
      <svg width="36" height="36" fill="none" viewBox="0 0 24 24" className="text-synorio-purple"><path d="M3 17h2v-7H3v7zm4 0h2v-4H7v4zm4 0h2V7h-2v10zm4 0h2v-2h-2v2z" fill="currentColor"/></svg>
    ),
    title: 'Analitika',
    desc: 'Možemo vam pomoći da bolje razumete svoje poslovanje kroz analizu ključnih pokazatelja performansi (KPI), jednokratna istraživanja i izgradnju prilagođenih reporting sistema. Cilj je da na osnovu vaših podataka dođemo do konkretnih uvida koji olakšavaju donošenje odluka ili unapređuju vaše poslovne rezultate.',
    bullets: [
      'Poslovna inteligencija',
      'Dashboardovi',
      'Vizualizacija podataka',
      'Izveštavanje',
    ],
  },
  {
    key: 'data',
    icon: (
      <svg width="36" height="36" fill="none" viewBox="0 0 24 24" className="text-synorio-purple"><path d="M12 4c-4.418 0-8 1.79-8 4v8c0 2.21 3.582 4 8 4s8-1.79 8-4v-8c0-2.21-3.582-4-8-4zm0 2c3.866 0 7 1.343 7 3s-3.134 3-7 3-7-1.343-7-3 3.134-3 7-3zm0 14c-3.866 0-7-1.343-7-3v-2c1.529 1.13 4.019 2 7 2s5.471-.87 7-2v2c0 1.657-3.134 3-7 3z" fill="currentColor"/></svg>
    ),
    title: 'Data inženjering',
    desc: 'Ako su vam podaci razbacani, polu‑strukturirani ili raspoređeni kroz više izvora – možemo ih dovesti u red i pretvoriti u vredan resurs spreman za analitiku, AI i automatizaciju. Pomažemo vam da izgradite efikasan i pouzdan data sistem koji se uklapa u vaše postojeće tehnologije – bez vezivanja za određenog provajdera, bilo da radite u cloudu ili na sopstvenoj infrastrukturi.',
    bullets: [
      'Arhitektura i dizajn podataka',
      'ETL / ELT procesi i orkestracija',
      'Integracije i streaming',
      'Skladištenje podataka',
    ],
  },
  {
    key: 'automation',
    icon: (
      <svg width="36" height="36" fill="none" viewBox="0 0 24 24" className="text-synorio-purple"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 14.93V19h-2v-2.07A8.001 8.001 0 014.07 13H2v-2h2.07A8.001 8.001 0 0111 4.07V2h2v2.07A8.001 8.001 0 0119.93 11H22v2h-2.07A8.001 8.001 0 0113 19.93z" fill="currentColor"/></svg>
    ),
    title: 'Automatizacije',
    desc: 'Ako vam se poslovni procesi oslanjaju na ručni rad, ponavljanje i kopiranje – možemo ih pojednostaviti i ubrzati kroz pametnu automatizaciju. Bilo da se radi o izveštavanju, obradi podataka ili svakodnevnoj operativi, pomažemo vam da uštedite radne sate, smanjite greške i oslobodite tim za zadatke koji zaista prave razliku.',
    bullets: [
      'Mapiranje procesa',
      'Izbor alata i integracija',
      'Automatski tokovi rada',
      'Monitoring i optimizacija',
    ],
  },
];

// Superhuman-style gradients (bazirano na slici)
const superhumanGradients = [
  'from-[#3a3d5c]/90 to-[#5a6b8a]/80', // plava
  'from-[#5a3a5c]/90 to-[#a259ff]/60', // ljubičasta
  'from-[#2d3c3c]/90 to-[#4cc9f0]/60', // teal
  'from-[#5c3a4c]/90 to-[#ff6a00]/60', // pink/orange
  'from-[#3a3d5c]/90 to-[#232347]/80', // siva
  'from-[#4c5c3a]/90 to-[#ffd200]/60', // žuta/zelena
  'from-[#3a4c5c]/90 to-[#4361ee]/60', // plava
  'from-[#5c3a3a]/90 to-[#ee0979]/60', // pink
];

// Ikonice za chipove po indeksu (primer: 1, 3, 5)
const chipIcons = [
  null,
  <Search className="w-4 h-4 mr-2 text-white/80 drop-shadow" />, // lupa
  null,
  <CreditCard className="w-4 h-4 mr-2 text-white/80 drop-shadow" />, // kartica
  null,
  <Lightbulb className="w-4 h-4 mr-2 text-yellow-300 drop-shadow" />, // sijalica
  null,
  null,
];

// Gradients za chipove
const chipGradients = [
  'from-[#a259ff] via-[#6e4cff] to-[#232347]', // ljubičasta
  'from-[#4cc9f0] via-[#4361ee] to-[#232347]', // plava
  'from-[#43e97b] via-[#38f9d7] to-[#232347]', // zelena
  'from-[#f7971e] via-[#ffd200] to-[#232347]', // narandžasta
  'from-[#ff6a00] via-[#ee0979] to-[#232347]', // pink/narandžasta
  'from-[#f953c6] via-[#b91d73] to-[#232347]', // pink
  'from-[#43cea2] via-[#185a9d] to-[#232347]', // teal
  'from-[#fa709a] via-[#fee140] to-[#232347]', // žuta/roze
];

// Novi tamni gradient za sve chipove
const chipGradient = 'from-[#2d1a4a] to-[#a259ff]';

// Tamni moderni gradijenti za chipove: ljubičasta, plava, siva, bordo
const superhumanRectGradients = [
  'bg-gradient-to-br from-[#2d1a4a] to-[#4b2067]', // tamna ljubičasta
  'bg-gradient-to-br from-[#232347] to-[#3a3d5c]', // tamno siva
  'bg-gradient-to-br from-[#1a223f] to-[#3a5ca8]', // tamno plava
  'bg-gradient-to-br from-[#3a3d5c] to-[#5a6b8a]', // sivo-plava
  'bg-gradient-to-br from-[#4b2067] to-[#a259ff]', // ljubičasta
  'bg-gradient-to-br from-[#2d1a4a] to-[#6e2142]', // bordo
  'bg-gradient-to-br from-[#232347] to-[#6e2142]', // sivo-bordo
  'bg-gradient-to-br from-[#1a223f] to-[#232347]', // tamno plava-siva
];

// TerminalTypewriter komponenta
const TerminalTypewriter = ({ lines }) => {
  const [displayed, setDisplayed] = useState([]);
  const [currentLine, setCurrentLine] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isBlink, setIsBlink] = useState(true);
  const typingSpeed = 28; // ms per char
  const lineDelay = 600; // ms between lines

  // Blinking cursor
  useEffect(() => {
    const blink = setInterval(() => setIsBlink(b => !b), 500);
    return () => clearInterval(blink);
  }, []);

  // Typewriter effect
  useEffect(() => {
    setDisplayed([]);
    setCurrentLine(0);
    setCurrentText('');
  }, [lines]);

  useEffect(() => {
    if (currentLine >= lines.length) return;
    let cancelled = false;
    let i = 0;
    function typeChar() {
      if (cancelled) return;
      if (i <= lines[currentLine].text.length) {
        setCurrentText(lines[currentLine].text.slice(0, i));
        i++;
        setTimeout(typeChar, typingSpeed);
      } else {
        setDisplayed(prev => [...prev, lines[currentLine]]);
        setCurrentLine(l => l + 1);
        setCurrentText('');
      }
    }
    if (currentText === '' && displayed.length === currentLine) {
      setTimeout(typeChar, lineDelay);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [currentLine, lines]);

  return (
    <div className="w-full h-full min-h-[180px] bg-[#181a20] border border-[#232347] rounded-2xl p-4 font-mono text-[15px] text-left text-[#e6e6e6] shadow-xl overflow-x-auto">
      {displayed.map((line, idx) => (
        <div key={idx} className="mb-1 flex items-center">
          <span className="text-synorio-purple select-none">{line.prompt}</span>
          <span className="ml-2">{line.text}</span>
        </div>
      ))}
      {currentLine < lines.length && (
        <div className="flex items-center">
          <span className="text-synorio-purple select-none">{lines[currentLine].prompt}</span>
          <span className="ml-2">{currentText}<span className={isBlink ? 'opacity-100' : 'opacity-0'}>|</span></span>
        </div>
      )}
    </div>
  );
};

// TerminalWindowWithControls komponenta
const TerminalWindowWithControls = ({ children }) => {
  return (
    <div className="w-full max-w-lg xl:max-w-xl min-h-[320px] xl:min-h-[440px] flex flex-col items-center justify-center">
      {/* Window bar sa tri kružića */}
      <div className="flex items-center gap-2 px-4 py-2 bg-[#232347] rounded-t-2xl w-full max-w-lg xl:max-w-xl relative">
        <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
        <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block"></span>
        <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
      </div>
      <div className="w-full max-w-lg xl:max-w-xl bg-[#181a20] border border-[#232347] rounded-b-2xl shadow-2xl p-6 xl:p-10 font-mono text-[16px] xl:text-[18px] text-left text-[#e6e6e6] overflow-x-auto">
        {children}
      </div>
    </div>
  );
};

// Helper za highlight
function highlightText(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={i} className="bg-synorio-purple/30 text-synorio-purple font-bold rounded px-1">{part}</mark> : part
  );
}

// ChatBubble komponenta za AI
function ChatBubble({ text, delay }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay || 0);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={show ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4 }}
      className="w-fit max-w-[90%] bg-[#232347] text-white rounded-2xl px-5 py-3 mb-3 shadow border border-synorio-purple/30"
      style={{ alignSelf: 'flex-start' }}
    >
      <span className="text-synorio-purple font-bold mr-2">🤖</span>{text}
    </motion.div>
  );
}
// MiniDashboard komponenta za Analitika (bez KPI kartica)
function MiniDashboard({ bullets }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full flex flex-col items-center gap-6">
      {/* Bar chart (placeholder) */}
      <div className="w-full max-w-xs h-32 flex items-end gap-2">
        {[60, 90, 40, 80, 55].map((v, i) => (
          <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${v}%` }} transition={{ delay: 0.2 + i * 0.1, duration: 0.5, type: 'spring' }} className="flex-1 bg-gradient-to-t from-synorio-purple/60 to-synorio-purple/90 rounded-t-lg shadow" />
        ))}
      </div>
      {/* Bullet list */}
      <ul className="mt-6 w-full max-w-xs mx-auto text-left">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-center gap-2 mb-2 text-white"><span className="w-2 h-2 rounded-full bg-synorio-purple inline-block"></span>{b}</li>
        ))}
      </ul>
    </motion.div>
  );
}
// Automatizacije vizuelni prikaz (stepper/timeline)
function AutomationStepper({ steps }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full flex flex-col items-center gap-6">
      <div className="flex flex-col gap-6 w-full max-w-xs mx-auto">
        {steps.map((step, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }} className="flex items-center gap-4">
            <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-synorio-purple/80 text-white text-xl shadow-lg border-2 border-synorio-purple/40`}>{step.icon}</div>
            <div className="text-white text-base font-semibold">{step.text}</div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// ResourceSavings komponenta za Automatizacije
function ResourceSavings() {
  const benefits = [
    { label: 'Vreme', icon: '⏱️', benefit: 'Brže izvršavanje zadataka' },
    { label: 'Troškovi', icon: '💰', benefit: 'Smanjenje operativnih troškova' },
    { label: 'Ljudski resursi', icon: '👥', benefit: 'Fokus na strateške zadatke' },
  ];

  return (
    <div className="flex justify-center items-center gap-4 sm:gap-8 py-4 text-center">
      {benefits.map((item) => (
        <div
          key={item.label}
          className="flex flex-col items-center justify-center bg-[rgba(30,24,54,0.85)] backdrop-blur-md rounded-xl p-4 sm:p-6 border border-[#2d1a4a]/40 shadow-md min-w-[110px] max-w-[150px]"
        >
          <div className="text-3xl mb-2">{item.icon}</div>
          <div className="text-base font-bold text-synorio-purple mb-1">{item.label}</div>
          <div className="text-gray-300 text-sm text-center leading-snug">{item.benefit}</div>
        </div>
      ))}
    </div>
  );
}

// AIChat komponenta za AI sekciju
function AIChat({ messages }) {
  const [shown, setShown] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (shown < messages.length) {
      const t = setTimeout(() => setShown(shown + 1), 700);
      return () => clearTimeout(t);
    } else if (shown === messages.length && !showTyping) {
      // Show 'AI is typing...' after a delay
      const t = setTimeout(() => setShowTyping(true), 1500);
      return () => clearTimeout(t);
    }
  }, [shown, messages.length, showTyping]);

  // Scroll to bottom on new message or typing
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [shown, showTyping]);

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col items-start justify-end gap-2 p-2 overflow-y-auto">
      {messages.slice(0, shown).map((msg, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: i * 0.1 }}
          className="flex items-end gap-2"
        >
          <span className="text-2xl select-none">🤖</span>
          <span className="bg-[#232347] text-white rounded-2xl px-5 py-3 shadow border border-synorio-purple/30 text-base max-w-[90%]">
            {msg}
          </span>
        </motion.div>
      ))}
      {/* AI is typing... */}
      {showTyping && (
        <div className="flex items-center gap-2 mt-2 ml-1">
          <span className="text-2xl select-none">🤖</span>
          <span className="text-gray-400 text-sm font-mono flex items-center">
            AI is typing
            <span className="ml-1 flex gap-0.5">
              <span className="animate-bounce [animation-delay:0ms]">.</span>
              <span className="animate-bounce [animation-delay:150ms]">.</span>
              <span className="animate-bounce [animation-delay:300ms]">.</span>
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

const Index: React.FC = () => {
  // FAQ data with categories
  const faqItems = [
    {
      question: "Kako implementirati AI u postojeće poslovne procese?",
      answer: "Implementacija AI u postojeće poslovne procese počinje detaljnom analizom trenutnih procedura, identifikacijom ključnih područja za optimizaciju, i postepenim uvođenjem AI rešenja uz redovnu evaluaciju rezultata i prilagođavanje prema potrebi.",
      category: "AI i ML"
    },
    {
      question: "Koliko vremena je potrebno za razvoj analitičkog sistema?",
      answer: "Vreme potrebno za razvoj analitičkog sistema zavisi od kompleksnosti zahteva i postojeće infrastrukture. Obično se kreće od nekoliko nedelja za osnovne implementacije do nekoliko meseci za složenije sisteme sa naprednim funkcionalnostima.",
      category: "Analitika"
    },
    {
      question: "Da li je potrebno posebno znanje za korišćenje vaših rešenja?",
      answer: "Naša rešenja su dizajnirana sa intuitivnim korisničkim interfejsima koji ne zahtevaju posebna tehnička znanja. Dodatno, obezbeđujemo kompletnu obuku i podršku za vas i vaš tim kako biste maksimalno iskoristili sve funkcionalnosti."
    },
    {
      question: "Kako se štite podaci u vašim analitičkim sistemima?",
      answer: "Bezbednost podataka je naš prioritet. Implementiramo višeslojnu zaštitu, uključujući enkripciju podataka, sigurnosne protokole pristupa, redovne sigurnosne provere i usklađenost sa svim relevantnim regulativama o zaštiti podataka."
    },
    {
      question: "Na koji način se meri povrat investicije (ROI) za AI projekte?",
      answer: "ROI za AI projekte merimo kroz više parametara: smanjenje operativnih troškova, povećanje efikasnosti procesa, unapređenje kvaliteta odlučivanja, skraćivanje vremena obrade, i povećanje zadovoljstva korisnika. Razvijamo prilagođene metrike koje odgovaraju specifičnim ciljevima vašeg poslovanja."
    },
    {
      question: "Kako izabrati pravi AI model za moj biznis?",
      answer: "Izbor AI modela zavisi od vaših poslovnih ciljeva, tipa podataka i željenih rezultata. Naš tim analizira vaše potrebe, predlaže optimalno rešenje i po potrebi radi razvoj i implementaciju AI/ML alata koji vam je potreban.",
      category: "AI i ML"
    },
    {
      question: "Da li mogu da koristim AI bez podataka",
      answer: "Možete koristiti AI i bez sopstvenih podataka, ali njegova primena tada ostaje u okvirima generičkih rešenja, koja nisu uvek precizna ni u skladu s vašim specifičnim poslovanjem. Možemo vam pomoći da iskoristite postojeće AI alate na pametan način i da ih prilagodite sebi, ali i da postavite strategiju za prikupljanje i strukturiranje podataka – kako biste u dogledno vreme imali sopstvenu bazu za unapređenje i finu adaptaciju AI rešenja prema vašim ciljevima.",
      category: "AI i ML"
    },
    {
      question: "Koje su prednosti korišćenja ML modela u svakodnevnom poslovanju?",
      answer: "ML modeli omogućavaju automatizaciju rutinskih zadataka, bolje predviđanje trendova i personalizaciju korisničkog iskustva.",
      category: "AI i ML"
    },
    {
      question: "Kako trenirate modele na našim podacima?",
      answer: "Modeli se treniraju na vašim podacima uz strogu zaštitu privatnosti i poštovanje svih bezbednosnih standarda.",
      category: "AI i ML"
    },
    {
      question: "Da li radite samo dashboarde ili i izveštaje?",
      answer: "Ne radimo samo vizuale — možemo pomoći i oko strukture izveštavanja, automatizacije, razumevanja KPI-jeva i same interpretacije rezultata. Pristupamo analitici kao podršci za donošenje odluka, ne samo kao “lepim grafikonima”.",
      category: "Analitika"
    },
    {
      question: "Koje tehnologije koristite?",
      answer: "Radimo sa širokim spektrom alata u zavisnosti od vašeg stacka – od Power BI i Tableau, preko Lookera i Metabasea, pa do SQL‑a, dbt‑a i Google Data Studija. Prilagođavamo se vašem okruženju, ne namećemo tehnologije.",
      category: "Analitika"
    },
    {
      question: "Možemo li raditi zajedno i ako već imamo tim analitičara?",
      answer: "Apsolutno. Možemo raditi kao podrška postojećem timu – bilo da je reč o razmeni znanja, postavljanju procesa, pomoći u razradi metrika ili jednostavno ubrzanju implementacije.",
      category: "Analitika"
    },
    {
      question: "Da li pomažete i u definisanju KPI‑jeva?",
      answer: "Da. Zapravo, to često bude prvi korak u saradnji. Prava analitika ne počinje alatom već pitanjima: šta merimo, zašto, i kako tačno znamo da nešto “radi”. Tu zajedno tražimo smisao pre nego što pređemo na metrike.",
      category: "Analitika"
    },
    {
      question: "Kako se vrši migracija podataka iz starih sistema?",
      answer: "Migracija podataka se planira pažljivo, uz testiranje i validaciju, kako bi se obezbedila potpuna i bezbedna tranzicija.",
      category: "Data inženjering"
    },
    {
      question: "Da li open-source alati mogu zameniti komercijalne platforme?",
      answer: "Da. Uz pravilnu arhitekturu i održavanje, open-source alati mogu biti pouzdana, skalabilna i isplativa alternativa komercijalnim rešenjima. Takođe pružaju veću fleksibilnost i nezavisnost od vendor lock-in-a.",
      category: "Data inženjering"
    },
    {
      question: "Da li radite i sa struktuiranim i nestruktuiranim podacima?",
      answer: "Da. Radimo sa različitim vrstama podataka – uključujući relacione (SQL), dokumentne (JSON, XML), vremenske serije, logove, tekstualne i multimedijalne podatke.",
      category: "Data inženjering"
    },
    {
      question: "Koliko traje tipična implementacija rešenja?",
      answer: "Implementacija traje od nekoliko nedelja do nekoliko meseci, u zavisnosti od kompleksnosti i zahteva projekta.",
      category: "Implementacija"
    },
    {
      question: "Da li je moguća integracija sa postojećim softverom?",
      answer: "Da, ukoliko postoji mogućnost integracije, možemo povezati naše rešenje sa vašim postojećim softverom.",
      category: "Implementacija"
    },
    {
      question: "Kako izgleda proces testiranja pre produkcije?",
      answer: "Sprovodimo detaljno testiranje funkcionalnosti, performansi i bezbednosti pre svake produkcione implementacije.",
      category: "Implementacija"
    },
    {
      question: "Da li ovo ima smisla za moj biznis",
      answer: "Ako donosite odluke, imate procese ili radite s podacima — verovatno ima. Ne mora svaki projekat biti veliki; često male optimizacije donesu najveću vrednost. Krenemo od onoga što vas trenutno najviše koči.",
      category: "Biznis"
    },
    {
      question: "Koliko brzo možemo videti rezultate",
      answer: "Brže nego što mislite. U zavisnosti od teme, prve uvide ili poboljšanja moguće je dobiti već u prvim nedeljama. Ne radimo projekte koji traju mesecima bez povratne vrednosti usput.",
      category: "Biznis"
    },
    {
      question: "Nismo sigurni šta nam tačno treba – da li je prerano da vas kontaktiramo?",
      answer: "Nije. Upravo tada je najbolje da pričamo. Možemo zajedno razjasniti gde su najveće prilike i kako da krenete pametno, bez nepotrebnog trošenja resursa.",
      category: "Biznis"
    },
    {
      question: "Da li nudite i konsultacije, bez izvođenja samog rada?",
      answer: "Da, radimo i kao konsultanti. Ako imate interni tim, ali vam treba pomoć oko strategije, prioritizacije, organizacije rada ili izbora tehnologije — možemo se uključiti bez potrebe da mi sve sami implementiramo.",
      category: "Biznis"
    },
    {
      question: "Kako merite uspeh projekta?",
      answer: "Uspeh merimo kroz konkretne promene: brže odluke, uštede u vremenu, smanjenje grešaka, rast prihoda ili jasniji uvidi. Definišemo zajedničke ciljeve na početku i pratimo ih tokom rada — bez generičnih “success metrics”.",
      category: "Biznis"
    },
    {
      question: "Da li nudite podršku nakon implementacije?",
      answer: "Da, obezbeđujemo tehničku podršku nakon implementacije rešenja.",
      category: "Tehnička podrška"
    },
    {
      question: "Kako mogu kontaktirati vaš tim za hitne slučajeve?",
      answer: "Naš tim je dostupan putem emaila i telefona za sve hitne slučajeve i podršku.",
      category: "Tehnička podrška"
    },
    {
      question: "Koje su opcije za obuku korisnika?",
      answer: "Nudimo online i onsite obuke, kao i detaljnu dokumentaciju za sve korisnike.",
      category: "Tehnička podrška"
    }
  ];

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Sve kategorije');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMember, setSelectedMember] = useState<null | typeof teamMembers[0]>(null);
  const [lastFocusedIdx, setLastFocusedIdx] = useState<number | null>(null);
  const avatarRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedFeature, setSelectedFeature] = useState(features[0]);
  const detailPanelRef = useRef(null);
  const modalContentRef = useRef(null);
  const isInView = useInView(detailPanelRef, { once: true, margin: '-100px' });
  const featureSectionRef = useRef(null);

  // Categories
  const categories = ['Sve kategorije', 'AI i ML', 'Analitika', 'Data inženjering', 'Implementacija', 'Biznis', 'Tehnička podrška'];

  // Responsive itemsPerPage for FAQ
  const getItemsPerPage = () => (typeof window !== 'undefined' && window.innerWidth < 640 ? 4 : 10);
  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage());

  React.useEffect(() => {
    function handleResize() {
      setItemsPerPage(getItemsPerPage());
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filtered and paginated FAQ items
  const filteredItems = useMemo(() => {
    return faqItems.filter(item => {
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'Sve kategorije' || item.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory, faqItems]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Split items for two columns
  const firstColumnItems = paginatedItems.slice(0, Math.ceil(paginatedItems.length / 2));
  const secondColumnItems = paginatedItems.slice(Math.ceil(paginatedItems.length / 2));

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle category change
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setCurrentPage(1); // Reset to first page on category change
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of FAQ section
    document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Escape zatvaranje detalja
  React.useEffect(() => {
    if (!selectedMember) return;
    const win = window as any;
    if (win.gtag) {
      win.gtag('event', 'modal_open', {
        event_category: 'Modal',
        event_label: selectedMember.name,
      });
    }
    function handleKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        const idx = teamMembers.findIndex(m => m.name === selectedMember.name);
        setSelectedMember(teamMembers[(idx - 1 + teamMembers.length) % teamMembers.length]);
        setLastFocusedIdx((idx - 1 + teamMembers.length) % teamMembers.length);
      } else if (e.key === 'ArrowRight') {
        const idx = teamMembers.findIndex(m => m.name === selectedMember.name);
        setSelectedMember(teamMembers[(idx + 1) % teamMembers.length]);
        setLastFocusedIdx((idx + 1) % teamMembers.length);
      } else if (e.key === 'Escape') {
        setSelectedMember(null);
        if (lastFocusedIdx !== null && avatarRefs.current[lastFocusedIdx]) {
          setTimeout(() => avatarRefs.current[lastFocusedIdx]?.focus(), 0);
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedMember, lastFocusedIdx, teamMembers]);

  // UseEffect for NeetoCal embed
  React.useEffect(() => {
    if (!document.getElementById('neetocal-embed-js')) {
      const script = document.createElement('script');
      script.id = 'neetocal-embed-js';
      script.async = true;
      script.src = 'https://synorio.neetocal.com/javascript/embed.js';
      document.body.appendChild(script);
    }
    const interval = setInterval(() => {
      if (window.neetoCal && window.neetoCal.embed) {
        // Target all buttons with id starting with 'open-popup-button'
        ['open-popup-button', 'open-popup-button-1', 'open-popup-button-2'].forEach(id => {
          if (document.getElementById(id)) {
            window.neetoCal.embed({
              type: 'elementClick',
              id: '220f06ba-8982-4c38-8cb9-f73e8216078c',
              organization: 'synorio',
              elementSelector: `#${id}`,
            });
          }
        });
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);
  
  // Feature click handler with auto-scroll on mobile
  const handleFeatureClick = (f) => {
    setSelectedFeature(f);
    const win = window as any;
    if (win.gtag) {
      win.gtag('event', 'tab_change', {
        event_category: 'Navigation',
        event_label: f.title,
      });
    }
    if (window.innerWidth < 1024 && featureSectionRef.current) {
      setTimeout(() => {
        featureSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };
  
  // Ref za tabove
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Scroll na aktivni tab
  React.useEffect(() => {
    const idx = features.findIndex(f => f.key === selectedFeature.key);
    if (tabRefs.current[idx]) {
      tabRefs.current[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedFeature, features]);
  
  // Google Analytics gtag.js SPA integracija
  useEffect(() => {
    const win = window as any;
    if (!win.gtagScriptLoaded) {
      const script = document.createElement('script');
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-ZE44S0BG3M';
      script.async = true;
      document.head.appendChild(script);
      win.gtagScriptLoaded = true;
    }
    win.dataLayer = win.dataLayer || [];
    function gtag(){win.dataLayer.push(arguments);}
    win.gtag = gtag;
    // @ts-ignore
    gtag('js', new Date());
    // @ts-ignore
    gtag('config', 'G-ZE44S0BG3M');
  }, []);
  
  return (
    <div className="min-h-screen text-white overflow-hidden">
      <Navbar />
      
      {/* Hero Section */}
      <Hero />
      
      {/* Partners Section */}
      <Section 
        id="partners-section"
        title="Ekspertiza građena u sledećim kompanijama"
        badgeTooltip="Logotipi su vlasništvo navedenih kompanija i koriste se isključivo u kontekstu prikaza profesionalnog iskustva."
      >
        <CompanyLogos />
      </Section>
      
      {/* Services Section - Intercom-style layout */}
      <Section 
        id="problems-section"
        title="Rešenja koja izrađujemo"
        className="py-12 sm:py-24"
      >
        <motion.div
          className="max-w-4xl mx-auto mb-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-300 text-lg leading-relaxed">
            Specijalizovani smo za rešavanje kompleksnih poslovnih izazova kroz implementaciju najnovijih tehnologija.
            Ne postoji univerzalno rešenje zbog toga nastojimo da personalnim pristupom dođemo do idealnog. Naša ekspertiza vam može pomoći podjednako prilikom verifikovanja ideje ili prilikom "puštanja u produkciju".
          </p>
        </motion.div>
        {/* Tabovi za izbor problema */}
        <div className="relative mb-6">
          {/* Mobile: Embla Carousel */}
          <div className="block lg:hidden">
            {/* Embla Carousel setup */}
            {(() => {
              const [emblaRef] = useEmblaCarousel({ loop: true, dragFree: true });
              return (
                <div className="embla" ref={emblaRef}>
                  <div className="embla__container flex gap-3 sm:gap-4 px-2 sm:px-4 py-1">
                    {features.map((f, idx) => (
                      <button
                        key={f.key}
                        onClick={() => handleFeatureClick(f)}
                        className={`min-w-[140px] flex-shrink-0 px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-150 border-2 focus:outline-none focus:ring-2 focus:ring-synorio-purple/60 mx-0.5
                          ${selectedFeature.key === f.key ? 'bg-synorio-purple text-white border-synorio-purple shadow relative after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-1 after:rounded-full after:bg-synorio-purple/80' : 'bg-gray-900/60 text-gray-200 border-transparent hover:bg-gray-900/80 hover:border-synorio-purple/40'}`}
                        tabIndex={0}
                        style={{ minWidth: '0' }}
                      >
                        <span className={`w-6 h-6 flex items-center justify-center ${selectedFeature.key === f.key ? 'text-white drop-shadow-[0_0_6px_#fff8]' : 'text-synorio-purple'}`}>{f.icon}</span>
                        <span className="whitespace-nowrap">{f.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          {/* Desktop: flex tab bar as before */}
          <div className="hidden lg:flex gap-3 sm:gap-4 w-full max-w-full px-2 sm:px-4 py-1 relative flex-wrap justify-center">
            {features.map((f, idx) => (
              <button
                key={f.key}
                ref={el => tabRefs.current[idx] = el}
                onClick={() => handleFeatureClick(f)}
                className={`min-w-[140px] flex-shrink-0 px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-150 border-2 focus:outline-none focus:ring-2 focus:ring-synorio-purple/60 mx-0.5
                  ${selectedFeature.key === f.key ? 'bg-synorio-purple text-white border-synorio-purple shadow relative after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-1 after:rounded-full after:bg-synorio-purple/80' : 'bg-gray-900/60 text-gray-200 border-transparent hover:bg-gray-900/80 hover:border-synorio-purple/40'}`}
                tabIndex={0}
                style={{ minWidth: '0' }}
              >
                <span className={`w-6 h-6 flex items-center justify-center ${selectedFeature.key === f.key ? 'text-white drop-shadow-[0_0_6px_#fff8]' : 'text-synorio-purple'}`}>{f.icon}</span>
                <span className="whitespace-nowrap">{f.title}</span>
              </button>
            ))}
          </div>
        </div>
        <div ref={featureSectionRef} className="w-full flex flex-col lg:flex-row gap-8 xl:gap-24 items-stretch justify-center mt-2 xl:mt-8">
          {/* Leva strana: naslov, opis, dugme */}
          <div className="flex-1 flex flex-col justify-center max-w-md xl:max-w-lg mx-auto order-1 lg:order-none px-2 sm:px-0 mt-0 xl:mt-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-left mb-4 sm:mb-6 leading-tight text-white">
              {selectedFeature.title}
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-4 sm:mb-6 text-left">{selectedFeature.desc}</p>
            <a
              id="open-popup-button-1"
              href="#"
              className="inline-flex items-center justify-center px-4 sm:px-6 py-3 rounded-xl bg-synorio-purple text-white font-bold text-base sm:text-lg shadow-lg hover:bg-synorio-purple/90 transition-colors w-full mb-6 focus:outline-none focus:ring-2 focus:ring-synorio-purple/60"
              style={{textDecoration: 'none'}}
              tabIndex={0}
              onClick={() => {
                const win = window as any;
                if (win.gtag) {
                  win.gtag('event', 'cta_click', {
                    event_category: 'CTA',
                    event_label: 'Zakaži besplatan sastanak',
                  });
                }
              }}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Zakaži besplatan sastanak
            </a>
          </div>
          {/* Desna strana: terminal samo na desktopu */}
          <div ref={detailPanelRef} className="flex-1 flex flex-col items-center justify-center order-2 lg:order-none px-2 sm:px-0">
            <TerminalWindowWithControls>
              {isInView && (
                selectedFeature.key === 'ai' ? (
                  <div className="max-w-md mx-auto h-[340px] sm:h-[400px] rounded-3xl border border-[#2d1a4a]/40 shadow-xl bg-[rgba(30,24,54,0.85)] backdrop-blur-md p-4 sm:p-10 flex flex-col justify-center items-start overflow-y-auto">
                    <AIChat messages={selectedFeature.bullets} />
                  </div>
                ) : selectedFeature.key === 'analytics' ? (
                  <div className="max-w-md mx-auto rounded-3xl border border-[#2d1a4a]/40 shadow-xl bg-[rgba(30,24,54,0.85)] backdrop-blur-md p-4 sm:p-10 flex flex-col justify-center items-center">
                    <MiniDashboard bullets={selectedFeature.bullets} />
                  </div>
                ) : selectedFeature.key === 'automation' ? (
                  <div className="max-w-md mx-auto flex flex-col justify-center items-center rounded-3xl border border-[#2d1a4a]/40 shadow-xl bg-[rgba(30,24,54,0.85)] backdrop-blur-md p-4 sm:p-10">
                    <ResourceSavings />
                  </div>
                ) : (
                  <div className="max-w-md mx-auto rounded-3xl border border-[#2d1a4a]/40 shadow-xl bg-[rgba(30,24,54,0.85)] backdrop-blur-md p-4 sm:p-10 flex flex-col justify-center items-center">
                    <TerminalTypewriter lines={selectedFeature.bullets.map((b, i) => ({ prompt: '$', text: b }))} />
                  </div>
                )
              )}
            </TerminalWindowWithControls>
          </div>
        </div>
      </Section>
      
      {/* About Us Section - avatar grid redesign */}
      <Section 
        id="about-section"
        title="O nama"
        className="py-12 sm:py-24"
      >
        <div className="container mx-auto flex flex-col lg:flex-row gap-12 items-stretch justify-center">
          {/* Leva strana: animated timeline/stepper */}
          <motion.div
            className="flex-1 flex flex-col justify-center items-start p-10 rounded-3xl mb-8 lg:mb-0 max-w-xl min-h-[420px] glass-panel-optimized shadow-2xl"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-col gap-10 w-full">
              {/* Timeline/stepper */}
              <div className="relative pl-8">
                {/* Vertical line */}
                <div className="absolute left-2 top-8 bottom-0 w-1 bg-gradient-to-b from-synorio-purple/60 to-transparent rounded-full" style={{height: 'calc(100% - 2rem)'}} />
                {/* Step 1 */}
                <div className="flex items-start gap-4 mb-8 group">
                  <div className="w-7 h-7 rounded-full bg-synorio-purple/80 flex items-center justify-center shadow-lg ring-2 ring-synorio-purple/30 group-hover:scale-110 transition-transform">
                    <Users className="w-4 h-4 text-white" />
            </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-synorio-purple mb-1">Ko smo mi?</h3>
                    <p className="text-gray-300 leading-relaxed">Mi smo Synorio — tim koji kombinuje analitiku, razvoj AI rešenja, data inženjering i automatizaciju kako bismo vam pomogli da izgradite održive proizvode, unapredite poslovne procese i donosite bolje odluke zasnovane na podacima. Dolazimo iz tech industrije, sa iskustvom u vođenju timova i projekata u oblastima analitike, veštačke inteligencije i sistemskih rešenja. Trenutno radimo direktno sa timovima koji žele da ubrzaju poslovanje, smanje greške i oslobode prostor za strateški rad.</p>
          </div>
            </div>
                {/* Step 2 */}
                <div className="flex items-start gap-4 mb-8 group">
                  <div className="w-7 h-7 rounded-full bg-synorio-purple/80 flex items-center justify-center shadow-lg ring-2 ring-synorio-purple/30 group-hover:scale-110 transition-transform">
                    <Target className="w-4 h-4 text-white" />
          </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-synorio-purple mb-1">Naša misija</h3>
                    <p className="text-gray-300 leading-relaxed">Naša misija je da kroz pametnu upotrebu podataka omogućimo brže inovacije i održive rezultate.</p>
            </div>
        </div>
                {/* Step 3 */}
                <div className="flex items-start gap-4 group">
                  <div className="w-7 h-7 rounded-full bg-synorio-purple/80 flex items-center justify-center shadow-lg ring-2 ring-synorio-purple/30 group-hover:scale-110 transition-transform">
                    <Heart className="w-4 h-4 text-white" />
            </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-synorio-purple mb-1">Naše vrednosti</h3>
                    <p className="text-gray-300 leading-relaxed">Otvorenost, inovativnost i posvećenost rezultatima su temelj našeg rada i odnosa sa klijentima.</p>
          </div>
                </div>
              </div>
            </div>
          </motion.div>
          {/* Desna strana: Swiper carousel za tim */}
          <div className="flex-1 flex flex-col justify-center items-center">
            <Swiper
              effect="coverflow"
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={1.2}
              coverflowEffect={{ rotate: 0, stretch: 0, depth: 180, modifier: 2.5, slideShadows: true }}
              pagination={{ clickable: true }}
              navigation={true}
              modules={[EffectCoverflow, Pagination, Navigation]}
              className="w-full max-w-2xl py-8"
            >
              {teamMembers.map((member, idx) => (
                <SwiperSlide key={member.name}>
                  <motion.div
                    className="about-avatar-card flex flex-col items-center w-full max-w-xs text-center px-6 py-8 min-h-[320px] cursor-pointer outline-none relative group transition-all duration-200 bg-[rgba(24,22,38,0.68)] rounded-2xl border-2 border-transparent hover:border-synorio-purple/70 hover:shadow-lg focus:border-synorio-purple/80 focus:shadow-lg shadow-xl"
                    whileHover={{ scale: 1.06 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 18 }}
                  >
                    <div className="about-avatar-img-wrapper mb-5">
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className="about-avatar-img w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-synorio-purple shadow-lg"
              loading="lazy"
            />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-synorio-purple/80 mb-1">{member.name}</h3>
                    <div className="text-synorio-purple font-semibold text-base md:text-lg mb-2">{member.title}</div>
                    <div className="flex gap-3 justify-center mb-3">
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-900/60 border border-gray-800 hover:bg-synorio-purple/20 transition-colors" aria-label={`LinkedIn ${member.name}`}>
                        <Linkedin className="w-5 h-5 text-synorio-purple" />
                      </a>
                      <a href={`mailto:${member.email}`} className="p-2 rounded-full bg-gray-900/60 border border-gray-800 hover:bg-synorio-purple/20 transition-colors" aria-label={`Email ${member.name}`}>
                        <Mail className="w-5 h-5 text-synorio-purple" />
              </a>
            </div>
                    <div className="text-gray-300 text-base leading-relaxed mb-2 text-center w-full max-w-md mx-auto">{member.description}</div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </Section>

      {/* FAQ Section */}
      <Section 
        id="faq-section"
        title="Česta pitanja"
        className="py-12 sm:py-24"
      >
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {/* Search and Filter Bar */}
          <motion.div
            className="mb-8 space-y-4"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Pretražite česta pitanja..."
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-synorio-purple/50 text-white placeholder-gray-400"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Category Filter - Embla Carousel on mobile */}
            <div className="block sm:hidden">
              {(() => {
                const [emblaRef] = useEmblaCarousel({ dragFree: true, loop: true });
                return (
                  <div className="embla" ref={emblaRef}>
                    <div className="embla__container flex gap-2 px-1 py-1">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                          className={`px-4 py-2 rounded-full shadow-sm font-semibold text-sm transition-colors duration-150 border-2 focus:outline-none focus:ring-2 focus:ring-synorio-purple/40 whitespace-nowrap
                            ${activeCategory === category
                              ? 'bg-synorio-purple text-white border-synorio-purple shadow'
                              : 'bg-gray-900/60 text-gray-200 border-transparent hover:bg-gray-900/80 hover:border-synorio-purple/40'}`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* Desktop: grid kao do sada */}
            <div className="hidden sm:flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 py-2 rounded-full shadow-sm font-semibold text-sm transition-colors duration-150 border-2 focus:outline-none focus:ring-2 focus:ring-synorio-purple/40 whitespace-nowrap
                    ${activeCategory === category
                      ? 'bg-synorio-purple text-white border-synorio-purple shadow'
                      : 'bg-gray-900/60 text-gray-200 border-transparent hover:bg-gray-900/80 hover:border-synorio-purple/40'}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>

          {/* FAQ Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {firstColumnItems.map((item, index) => (
              <motion.div
                key={`faq-${index}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                  <AccordionItem 
                    value={`item-${index}`} 
                    className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 backdrop-blur-sm hover:bg-gray-900/50 transition-colors duration-200"
                  >
                    <AccordionTrigger className="text-left px-6 py-5 text-white hover:text-synorio-purple hover:no-underline text-lg font-medium"
                      onClick={() => {
                        const win = window as any;
                        if (win.gtag) {
                          win.gtag('event', 'faq_open', {
                            event_category: 'FAQ',
                            event_label: item.question,
                          });
                        }
                      }}
                    >
                      {highlightText(item.question, searchQuery)}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-5 text-gray-300 leading-relaxed relative">
                      {/* Copy to clipboard */}
                      <CopyToClipboardButton text={item.answer} />
                      <div className="mb-3">{highlightText(item.answer, searchQuery)}</div>
                      <FaqFeedback />
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>

            <Accordion type="single" collapsible className="w-full space-y-2">
              {secondColumnItems.map((item, index) => (
                <motion.div
                  key={`faq-${index + firstColumnItems.length}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <AccordionItem 
                    value={`item-${index + firstColumnItems.length}`} 
                    className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 backdrop-blur-sm hover:bg-gray-900/50 transition-colors duration-200"
                  >
                    <AccordionTrigger className="text-left px-6 py-5 text-white hover:text-synorio-purple hover:no-underline text-lg font-medium"
                      onClick={() => {
                        const win = window as any;
                        if (win.gtag) {
                          win.gtag('event', 'faq_open', {
                            event_category: 'FAQ',
                            event_label: item.question,
                          });
                        }
                      }}
                    >
                    {highlightText(item.question, searchQuery)}
                  </AccordionTrigger>
                    <AccordionContent className="px-6 pb-5 text-gray-300 leading-relaxed relative">
                    {/* Copy to clipboard */}
                    <CopyToClipboardButton text={item.answer} />
                    <div className="mb-3">{highlightText(item.answer, searchQuery)}</div>
                    <FaqFeedback />
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              {/* Mobile: minimal prev/next with counter */}
              <div className="block sm:hidden w-full flex items-center justify-center gap-3">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-900/60 text-synorio-purple text-xl font-bold shadow hover:bg-synorio-purple/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Prethodna strana"
                >
                  ←
                </button>
                <span className="text-base font-semibold text-gray-300 select-none min-w-[90px] text-center">
                  Strana {currentPage} od {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-900/60 text-synorio-purple text-xl font-bold shadow hover:bg-synorio-purple/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Sledeća strana"
                >
                  →
                </button>
              </div>
              {/* Desktop: flex pagination as before */}
              <div className="hidden sm:flex justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-900/50 text-gray-300 rounded-lg hover:bg-gray-900/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prethodna
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-synorio-purple/20 text-synorio-purple'
                      : 'bg-gray-900/50 text-gray-300 hover:bg-gray-900/70'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-900/50 text-gray-300 rounded-lg hover:bg-gray-900/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sledeća
              </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </Section>

      {/* Contact Section */}
      <Section 
        id="contact-section"
        title="Stupite u kontakt"
        className="py-12 sm:py-24"
      >
        <div className="container px-4 py-8 bg-gray-900/60 backdrop-blur-md border border-gray-800 rounded-2xl shadow-xl">
          <h3 className="text-2xl font-bold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-white to-synorio-purple">
            Pošaljite nam poruku
          </h3>
          <ContactForm />
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-10 bg-transparent border-t-0">
        <div className="container px-4 md:px-6 flex flex-col items-center justify-center text-center">
          <NewsletterSubscribe />
          <motion.p
            className="text-base md:text-lg text-gray-400 font-medium mb-2"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Napravljeno sa <span className="mx-1">☕</span> i <span className="mx-1">💜</span> od strane tri entuzijasta podataka koji žele da pomognu kompanijama da bolje koriste svoje podatke.
          </motion.p>
          <div className="text-xs text-gray-600 mt-2">
            Srđan, Aleksandar & Vukašin &middot; © {new Date().getFullYear()} Synorio
          </div>
        </div>
      </footer>

      {/* Mobile modal for member details */}
      {selectedMember && (
        <>
          {/* Mobile modal only */}
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center sm:hidden"
            onClick={() => {
              setSelectedMember(null);
              if (lastFocusedIdx !== null && avatarRefs.current[lastFocusedIdx]) {
                setTimeout(() => avatarRefs.current[lastFocusedIdx]?.focus(), 0);
              }
            }}
          >
            <div
              ref={modalContentRef}
              className="relative w-full max-w-md mx-auto bg-[rgba(24,22,38,0.98)] rounded-3xl p-6 shadow-2xl flex flex-col items-center"
              onClick={e => e.stopPropagation()}
            >
              {/* X button for close */}
              <button
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-synorio-purple/90 text-white flex items-center justify-center text-2xl shadow-lg"
                onClick={() => {
                  setSelectedMember(null);
                  if (lastFocusedIdx !== null && avatarRefs.current[lastFocusedIdx]) {
                    setTimeout(() => avatarRefs.current[lastFocusedIdx]?.focus(), 0);
                  }
                }}
                aria-label="Zatvori"
              >
                ×
              </button>
              <img src={selectedMember.avatar} alt={selectedMember.name} className="w-28 h-28 rounded-full object-cover border-4 border-synorio-purple shadow-lg mb-2" width={112} height={112} />
              <h3 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-synorio-purple/80">{selectedMember.name}</h3>
              <div className="text-synorio-purple font-semibold text-base md:text-lg mb-2">{selectedMember.title}</div>
              <div className="flex gap-3 justify-center mb-2">
                <a href={selectedMember.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-900/60 border border-gray-800 hover:bg-synorio-purple/20 transition-colors" aria-label={`LinkedIn ${selectedMember.name}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-synorio-purple" fill="none" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.28c-.97 0-1.75-.79-1.75-1.75s.78-1.75 1.75-1.75 1.75.79 1.75 1.75-.78 1.75-1.75 1.75zm13.5 10.28h-3v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39v4.58h-3v-9h2.89v1.23h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v4.72z"/>
                  </svg>
                </a>
                <a href={`mailto:${selectedMember.email}`} className="p-2 rounded-full bg-gray-900/60 border border-gray-800 hover:bg-synorio-purple/20 transition-colors" aria-label={`Email ${selectedMember.name}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-synorio-purple" fill="none" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 2v.01L12 13 4 6.01V6h16zm0 12H4V8.99l8 6.99 8-6.99V18z"/>
                  </svg>
                </a>
              </div>
              <div className="text-gray-300 text-base leading-relaxed mb-2 text-left w-full max-w-md mx-auto">{selectedMember.description}</div>
              {/* Prev/Next buttons on mobile */}
              <div className="flex justify-between w-full mt-6">
                <button
                  onClick={() => {
                    const idx = teamMembers.findIndex(m => m.name === selectedMember.name);
                    setSelectedMember(teamMembers[(idx - 1 + teamMembers.length) % teamMembers.length]);
                    setLastFocusedIdx((idx - 1 + teamMembers.length) % teamMembers.length);
                  }}
                  className="w-1/3 py-3 rounded-xl bg-synorio-purple/20 text-synorio-purple font-bold text-lg shadow hover:bg-synorio-purple/30 transition-colors"
                >
                  ← Prethodni
                </button>
                <button
                  onClick={() => {
                    const idx = teamMembers.findIndex(m => m.name === selectedMember.name);
                    setSelectedMember(teamMembers[(idx + 1) % teamMembers.length]);
                    setLastFocusedIdx((idx + 1) % teamMembers.length);
                  }}
                  className="w-1/3 py-3 rounded-xl bg-synorio-purple/20 text-synorio-purple font-bold text-lg shadow hover:bg-synorio-purple/30 transition-colors"
                >
                  Sledeći →
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// CopyToClipboardButton komponenta
function CopyToClipboardButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="absolute top-3 right-3 p-2 rounded bg-gray-800/60 hover:bg-synorio-purple/30 transition-colors text-gray-300 hover:text-synorio-purple flex items-center gap-1 text-xs"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      aria-label="Kopiraj odgovor"
      type="button"
    >
      {copied ? <><Check className="w-4 h-4" /> Kopirano</> : <><Clipboard className="w-4 h-4" /> Kopiraj</>}
    </button>
  );
}

// FaqFeedback komponenta
function FaqFeedback() {
  const [feedback, setFeedback] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [comment, setComment] = useState('');
  return (
    <div className="mt-4">
      {!feedback && (
        <div className="flex items-center gap-3 text-sm">
          <span>Da li je ovaj odgovor bio koristan?</span>
          <button className="p-2 rounded-full bg-gray-800/60 hover:bg-green-500/30 transition-colors" onClick={() => setFeedback('yes')} aria-label="Da"><ThumbsUp className="w-4 h-4" /></button>
          <button className="p-2 rounded-full bg-gray-800/60 hover:bg-red-500/30 transition-colors" onClick={() => setFeedback('no')} aria-label="Ne"><ThumbsDown className="w-4 h-4" /></button>
        </div>
      )}
      {feedback === 'yes' && <div className="text-green-400 mt-2 animate-fade-in">Hvala na povratnoj informaciji! 😊</div>}
      {feedback === 'no' && (
        <div className="mt-2 animate-fade-in">
          <div className="text-red-400 mb-2">Žao nam je! Možete li nam reći šta ste tražili?</div>
          {!showForm ? (
            <button className="px-3 py-1 rounded bg-gray-800/60 hover:bg-synorio-purple/30 text-synorio-purple text-xs" onClick={() => setShowForm(true)}>Ostavi komentar</button>
          ) : (
            <form className="flex flex-col gap-2 mt-2" onSubmit={e => { e.preventDefault(); setFeedback('done'); }}>
              <textarea className="bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white" rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Vaš komentar..." />
              <button className="self-end px-3 py-1 rounded bg-synorio-purple text-white text-xs" type="submit">Pošalji</button>
            </form>
          )}
        </div>
      )}
      {feedback === 'done' && <div className="text-synorio-purple mt-2 animate-fade-in">Hvala! Vaš komentar je zabeležen.</div>}
    </div>
  );
}

export default Index;
